import { createFileRoute, Link } from "@tanstack/react-router";
import arvRenovationHero from "@/assets/hero/hero-arv-renovation.jpg";
import { useEffect, useMemo, useRef } from "react";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand, FAQ, SectionHeader } from "@/components/site/Blocks";
import { CalcCard, CalcField, CalcRow, CalcStat, usd, pct } from "@/components/site/CalcKit";
import { ToolLeadGate } from "@/components/site/ToolLeadGate";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { ShareResultsBar } from "@/components/site/ShareResultsBar";
import { CalculatorAiPanel } from "@/components/site/CalculatorAiPanel";
import { CalculatorStateGate } from "@/components/site/CalculatorStateGate";
import { trackCalcCta, trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const FAQS = [
  {
    q: "What does ARV mean in real estate?",
    a: "ARV stands for after-repair value — the price a property should sell for once the planned renovation is complete and the home is in typical retail condition for its submarket. Lenders on fix-and-flip and bridge programs size loans against ARV determined by an appraisal or broker price opinion, not by the borrower's estimate.",
  },
  {
    q: "How do I calculate ARV?",
    a: "Take three to five recent sold comparables within roughly half a mile, closed in the last three to six months, similar in bedroom and bathroom count, square footage, and lot. Convert each to a price per square foot in renovated condition, adjust for material differences, then apply the adjusted average to your subject's finished square footage. Pending sales and active listings inform direction but are not comps.",
  },
  {
    q: "What is the 70% rule?",
    a: "The 70% rule says maximum offer = (ARV × 70%) − estimated rehab. The 30% haircut is meant to absorb holding costs, financing, selling costs, and profit. It is a screening filter, not underwriting: in low-price markets the fixed costs consume more than 30%, and in high-priced markets the rule can be conservative.",
  },
  {
    q: "How much does a lender lend against ARV?",
    a: "Fix-and-flip and bridge programs commonly cap at a percentage of purchase plus a percentage of the rehab budget, with an overall cap measured against ARV. The specific caps depend on the program, borrower experience, credit, and property. Any figures here are illustrative estimates and not an offer of credit or a commitment to lend.",
  },
  {
    q: "Is ARV the same as appraised value?",
    a: "No. Your ARV is an estimate. The appraised ARV is an opinion of value prepared by a licensed appraiser using a scope-of-work and the same comparable-sales approach, and it is what a lender will size against. Deals fail most often when the borrower's ARV was optimistic relative to the appraisal.",
  },
  {
    q: "What comps should I avoid using for ARV?",
    a: "Skip distressed sales, foreclosures, short sales, and off-market wholesale flips, since these prices reflect seller duress rather than open-market retail value. Also avoid new construction unless the subject will be finished to a comparable spec, and avoid comps outside the same school zone or HOA if those factors move price locally.",
  },
  {
    q: "How does square footage affect ARV accuracy?",
    a: "Price per square foot compresses as homes get larger and expands for smaller homes, so applying one flat rate across very different sizes overstates or understates value. Pull comps closest to your subject's square footage and adjust the per-square-foot rate incrementally rather than linearly for big size gaps.",
  },
  {
    q: "Can ARV be too high for a lender to approve rehab draws?",
    a: "Yes. Draw-based fix-and-flip and bridge financing releases funds against completed work verified by inspection, and the total loan is capped relative to ARV regardless of how the borrower's estimate compares. If the appraised ARV comes in below the borrower's number, draws and the total loan amount are typically resized to the lower figure.",
  },
  {
    q: "How do rehab budget overruns affect the 70% rule outcome?",
    a: "Because maximum offer is ARV times 70% minus rehab, every dollar of rehab overrun reduces your effective margin dollar for dollar rather than being absorbed elsewhere. Experienced flippers typically build a 10%–15% contingency into the rehab line before applying the rule so a change order does not erase the profit margin.",
  },
  {
    q: "Does ARV differ for a rental exit versus a resale exit?",
    a: "Yes. A resale ARV reflects retail buyer comps in finished, staged condition, while a rental exit is often underwritten off an as-repaired appraisal that also considers rent comparables for a DSCR refinance. Investors planning a BRRRR strategy should run both the resale ARV and the rental-appraisal value since a refinance lender will use its own appraisal.",
  },
  {
    q: "How many comps are enough to support an ARV estimate?",
    a: "Three comps is the practical minimum and five or more strengthens the estimate, especially in submarkets with wide price dispersion. Weight the most recent and most similar sales more heavily, and cross-check with any pending sales for directional confirmation even though pendings are not closed comps.",
  },
  {
    q: "What happens if the appraisal comes in below my ARV estimate?",
    a: "A low appraisal typically reduces the loan amount, since draw and total loan sizing are tied to the appraised ARV rather than the borrower's projection. Any figures from this calculator are illustrative estimates only, and actual loan terms remain subject to credit review, appraisal, and full underwriting.",
  },
];

export const Route = createFileRoute("/arv-calculator")({
  head: () =>
    buildHead({
      title: "ARV Calculator — After Repair Value & 70% Rule",
      description:
        "Free ARV calculator: estimate after-repair value from comps, apply the 70% rule, and see maximum allowable offer, all-in cost, projected profit and ROI on a flip.",
      url: "/arv-calculator",
      keywords:
        "ARV calculator, after repair value calculator, 70 percent rule calculator, maximum allowable offer calculator, MAO calculator, house flipping calculator, ARV formula, how to calculate ARV, fix and flip profit calculator, rehab budget calculator, comps price per square foot",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "ARV Calculator", url: "/arv-calculator" },
      ],
      service: { name: "After-Repair Value (ARV) Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "ARV Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/arv-calculator",
          description:
            "Free after-repair value calculator with comp-based ARV, the 70% rule, maximum allowable offer, and flip profit projection.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: ArvPage,
});

type Comp = { price: number; sqft: number };

type Inputs = {
  subjectSqft: number;
  comps: Comp[];
  adjustmentPct: number;
  rehab: number;
  purchasePrice: number;
  holdMonths: number;
  monthlyCarry: number;
  buyClosingPct: number;
  sellCostPct: number;
  rulePct: number;
};

const DEFAULT: Inputs = {
  subjectSqft: 1650,
  comps: [
    { price: 315000, sqft: 1580 },
    { price: 342000, sqft: 1720 },
    { price: 328000, sqft: 1640 },
  ],
  adjustmentPct: 0,
  rehab: 55000,
  purchasePrice: 165000,
  holdMonths: 6,
  monthlyCarry: 1850,
  buyClosingPct: 2,
  sellCostPct: 7,
  rulePct: 70,
};

function ArvPage() {
  const { state: i, setState: setI, patch, shareUrl } = useShareableCalcState<Inputs>("arv", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("arv", String(k), v as never);
  };
  const setComp = (idx: number, changes: Partial<Comp>) => {
    setI((p) => ({ ...p, comps: p.comps.map((c, n) => (n === idx ? { ...c, ...changes } : c)) }));
    trackCalcInput("arv", `comp_${idx}`, Object.values(changes)[0] ?? 0);
  };

  const m = useMemo(() => {
    const valid = i.comps.filter((c) => c.price > 0 && c.sqft > 0);
    const ppsfList = valid.map((c) => c.price / c.sqft);
    const avgPpsf = ppsfList.length ? ppsfList.reduce((a, b) => a + b, 0) / ppsfList.length : 0;
    const adjPpsf = avgPpsf * (1 + i.adjustmentPct / 100);
    const arv = adjPpsf * i.subjectSqft;

    const mao = arv * (i.rulePct / 100) - i.rehab;
    const buyClosing = i.purchasePrice * (i.buyClosingPct / 100);
    const carry = i.monthlyCarry * i.holdMonths;
    const sellCosts = arv * (i.sellCostPct / 100);
    const allIn = i.purchasePrice + i.rehab + buyClosing + carry;
    const profit = arv - allIn - sellCosts;
    const roi = allIn > 0 ? profit / allIn : 0;
    const marginOnArv = arv > 0 ? profit / arv : 0;
    const passes70 = i.purchasePrice <= mao;

    return { avgPpsf, adjPpsf, arv, mao, buyClosing, carry, sellCosts, allIn, profit, roi, marginOnArv, passes70, compCount: valid.length };
  }, [i]);

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackCalcEstimateViewed("arv", { arv: Math.round(m.arv), comps: m.compCount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Comps used", value: String(m.compCount) },
    { label: "Average comp $/sq ft", value: usd(m.avgPpsf) },
    { label: "Adjusted $/sq ft", value: usd(m.adjPpsf) },
    { label: "Estimated ARV", value: usd(m.arv) },
    { label: "Maximum allowable offer (70% rule)", value: usd(m.mao) },
    { label: "All-in cost", value: usd(m.allIn) },
    { label: "Projected profit", value: usd(m.profit) },
    { label: "Return on investment", value: pct(m.roi) },
  ];


  return (
    <SiteShell>
      <PageHero
        eyebrow="ARV calculator"
        title="ARV Calculator: After-Repair Value and the 70% Rule"
        subtitle="Estimate after-repair value from your sold comps, screen the deal against the 70% rule, and project all-in cost, profit and ROI on a flip. Figures are illustrative estimates, not an appraisal or an offer of credit."
        image={{ src: arvRenovationHero, alt: "Two property owners reviewing renovation progress with a contractor inside a home under rehab" }}
        primaryHref="/apply?product=fix-and-flip-loans"
        primaryLabel="Get Your Personalized Rate Quote"
        secondaryHref="/fix-and-flip-loans"
        secondaryLabel="Fix & Flip Financing"
      />
      <PageEssentials />

      <Section>
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-6">
            <CalcCard title="1. Subject property & sold comps">
              <CalcField label="Subject finished square footage" value={i.subjectSqft} onChange={(v) => set("subjectSqft", v)} step={10} />
              <div className="mt-4 space-y-3">
                {i.comps.map((c, idx) => (
                  <div key={idx} className="grid gap-3 sm:grid-cols-3">
                    <CalcField label={`Comp ${idx + 1} sale price`} value={c.price} onChange={(v) => setComp(idx, { price: v })} prefix="$" />
                    <CalcField label={`Comp ${idx + 1} sq ft`} value={c.sqft} onChange={(v) => setComp(idx, { sqft: v })} step={10} />
                    <div className="flex items-end pb-2 text-sm text-muted-foreground tabular-nums">
                      {c.sqft > 0 ? `$${(c.price / c.sqft).toFixed(0)}/sq ft` : "—"}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-3">
                <button
                  type="button"
                  onClick={() => setI((p) => ({ ...p, comps: [...p.comps, { price: 0, sqft: 0 }] }))}
                  className="rounded-md border border-input px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
                >
                  Add comp
                </button>
                {i.comps.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setI((p) => ({ ...p, comps: p.comps.slice(0, -1) }))}
                    className="rounded-md border border-input px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted"
                  >
                    Remove last
                  </button>
                )}
              </div>
              <div className="mt-4">
                <CalcField
                  label="Condition / location adjustment"
                  value={i.adjustmentPct}
                  onChange={(v) => set("adjustmentPct", v)}
                  suffix="%"
                  step={1}
                  hint="Negative if your subject is inferior to the comps (busier street, smaller lot, no garage)."
                />
              </div>
            </CalcCard>

            <CalcCard title="2. Deal costs">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Purchase price" value={i.purchasePrice} onChange={(v) => set("purchasePrice", v)} prefix="$" />
                <CalcField label="Rehab budget" value={i.rehab} onChange={(v) => set("rehab", v)} prefix="$" />
                <CalcField label="Buy-side closing costs" value={i.buyClosingPct} onChange={(v) => set("buyClosingPct", v)} suffix="%" step={0.25} />
                <CalcField label="Hold time (months)" value={i.holdMonths} onChange={(v) => set("holdMonths", v)} step={1} />
                <CalcField label="Monthly carry (interest, taxes, insurance, utilities)" value={i.monthlyCarry} onChange={(v) => set("monthlyCarry", v)} prefix="$" />
                <CalcField label="Sell-side costs" value={i.sellCostPct} onChange={(v) => set("sellCostPct", v)} suffix="%" step={0.25} hint="Commissions, seller concessions, transfer taxes." />
              </div>
            </CalcCard>

            <CalcCard title="3. Offer rule">
              <CalcField label="Rule percentage of ARV" value={i.rulePct} onChange={(v) => set("rulePct", v)} suffix="%" step={1} hint="70% is the classic screen. Experienced operators in high-price markets often use 75–80%." />
            </CalcCard>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <CalculatorStateGate calculator="arv">
            <ToolLeadGate
              calculator="arv"
              storageKey="arv-calc:lead-unlocked"
              product="ARV Calculator"
              sourcePage="ARV Calculator"
              title="Unlock your ARV and flip analysis"
              subtitle="See estimated ARV, maximum allowable offer, all-in cost, projected profit and ROI — and we'll email you a copy."
              getContext={() => ({
                subject_sqft: i.subjectSqft,
                comp_count: m.compCount,
                estimated_arv: Math.round(m.arv),
                rehab: i.rehab,
                purchase_price: i.purchasePrice,
                max_offer: Math.round(m.mao),
                projected_profit: Math.round(m.profit),
              })}
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">Estimated ARV</div>
                  <div className="mt-1 font-display text-3xl font-bold tabular-nums text-foreground">{usd(m.arv)}</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Based on {m.compCount} comp{m.compCount === 1 ? "" : "s"} averaging ${m.avgPpsf.toFixed(0)}/sq ft
                    {i.adjustmentPct !== 0 ? `, adjusted ${i.adjustmentPct > 0 ? "+" : ""}${i.adjustmentPct}% to $${m.adjPpsf.toFixed(0)}/sq ft` : ""}.
                    A lender will size against an appraised ARV, not this estimate.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <CalcStat label={`Max offer (${i.rulePct}% rule)`} value={usd(m.mao)} colorClass={m.passes70 ? "text-emerald-600" : "text-destructive"} />
                  <CalcStat label="Projected profit" value={usd(m.profit)} colorClass={m.profit >= 0 ? "text-emerald-600" : "text-destructive"} />
                  <CalcStat label="ROI on all-in cost" value={pct(m.roi, 1)} />
                  <CalcStat label="Margin on ARV" value={pct(m.marginOnArv, 1)} />
                </div>

                <div className="rounded-md border border-brand-gold/40 bg-brand-gold/5 p-3 text-xs text-foreground">
                  {m.passes70
                    ? `Your ${usd(i.purchasePrice)} purchase price is at or under the ${i.rulePct}% screen.`
                    : `Your purchase price is ${usd(i.purchasePrice - m.mao)} above the ${i.rulePct}% screen — the margin is thin.`}
                </div>

                <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <h3 className="font-display text-base font-semibold text-foreground">Cost breakdown</h3>
                  <dl className="mt-3 space-y-1.5 text-sm">
                    <CalcRow label="Purchase price" value={usd(i.purchasePrice)} />
                    <CalcRow label="Rehab budget" value={usd(i.rehab)} />
                    <CalcRow label="Buy-side closing costs" value={usd(m.buyClosing)} />
                    <CalcRow label={`Carry (${i.holdMonths} months)`} value={usd(m.carry)} />
                    <CalcRow label="All-in cost" value={usd(m.allIn)} strong />
                    <CalcRow label="Sell-side costs" value={usd(m.sellCosts)} />
                    <CalcRow label="Projected net profit" value={usd(m.profit)} strong colorClass={m.profit >= 0 ? "text-emerald-600" : "text-destructive"} />
                  </dl>
                </div>

                <Link
                  onClick={() => trackCalcCta("arv", "related_tool")}
                  to="/brrrr-calculator"
                  className="block rounded-md bg-brand px-4 py-3 text-center text-sm font-semibold text-primary-foreground hover:bg-brand/90"
                >
                  Keep it as a rental instead: BRRRR →
                </Link>
                <Link
                  onClick={() => trackCalcCta("arv", "apply")}
                  to="/apply"
                  className="block rounded-md border border-brand-gold bg-brand-gold px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-brand-gold-hover"
                >
                  Get Your Personalized Rate Quote
                </Link>
              </div>
            </ToolLeadGate>
            <ShareResultsBar
              calculator="arv"
              shareUrl={shareUrl}
              subject="My ARV scenario"
              summaryTitle="ARV scenario — key numbers"
              keyNumbers={aiFacts.slice(0, 6)}
            />
            <CalculatorAiPanel
              calculator="arv"
              facts={aiFacts}
              suggestions={[
                  "How should I adjust comps for condition and size?",
                  "What is the 70% rule and when does it break down?",
                  "What costs belong in an all-in number?",
                  "How does ARV affect a refinance later?",
                ]}
            />
            </CalculatorStateGate>
          </aside>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl space-y-4 text-sm text-foreground/90">
          <SectionHeader
            title="How to pull comps that hold up at appraisal"
            intro="Your ARV is only as good as the sales behind it. This is the comp standard appraisers apply."
          />
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>Closed sales only.</strong> Actives and pendings show direction; they are not evidence of value.</li>
            <li><strong>Recent.</strong> Three to six months in a stable market, tighter when prices are moving.</li>
            <li><strong>Close in.</strong> Half a mile in a suburb, same subdivision where one exists, and never across a school-district or municipal boundary.</li>
            <li><strong>Similar.</strong> Within roughly 20% on square footage, same bedroom count, same story configuration, similar lot and garage.</li>
            <li><strong>Renovated condition.</strong> Compare your finished product to finished product — not to distressed sales, which is how ARV gets overstated.</li>
            <li><strong>Adjust honestly.</strong> Fewer bathrooms, a busier street, or no garage are real deductions. Build the deduction in before you offer, not after the appraisal.</li>
          </ul>
          <p>
            Once the ARV is defensible, the financing question is simple: a{" "}
            <Link to="/fix-and-flip-loans" className="text-brand underline">fix-and-flip loan</Link> funds purchase plus rehab
            draws for a resale exit, while a{" "}
            <Link to="/dscr-loans" className="text-brand underline">DSCR loan</Link> is the takeout if you keep the property as a
            rental. The <Link to="/brrrr-calculator" className="text-brand underline">BRRRR calculator</Link> models that
            handoff end to end.
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl">
          <SectionHeader title="ARV and 70% rule FAQ" intro="What investors and lenders actually mean by after-repair value." />
          <div className="mt-6">
            <FAQ items={FAQS} />
          </div>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-foreground">Related investor tools</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/brrrr-calculator" as const, label: "BRRRR calculator" },
              { to: "/dscr-calculator" as const, label: "DSCR calculator" },
              { to: "/cash-on-cash-return-calculator" as const, label: "Cash-on-cash return calculator" },
              { to: "/cap-rate-calculator" as const, label: "Cap rate calculator" },
              { to: "/fix-and-flip-loans" as const, label: "Fix & flip financing" },
              { to: "/best-rental-markets" as const, label: "Best rental markets" },
            ].map((l) => (
              <li key={l.to}><Link to={l.to} className="text-brand underline">{l.label}</Link></li>
            ))}
          </ul>
        </div>
      </Section>

      <CTABand
        title="Financing for the rehab and the takeout"
        body="Send the scenario and we'll return illustrative bridge and DSCR structures for it — business-purpose investment property only, subject to full underwriting."
      />
    <PageOutro />
      </SiteShell>
  );
}
